import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Stack,
  IconButton,
  alpha,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { getWeeklyScheduleLogs } from "../../../api/services/settings";
import { useQuery } from "react-query";

const SectionHeader = ({ icon: Icon, label, count, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    <Icon sx={{ fontSize: 16, color }} />
    <Typography
      variant="caption"
      fontWeight={700}
      color="#475467"
      sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
    >
      {label} {typeof count === "number" && `(${count})`}
    </Typography>
  </Box>
);

const SubTable = ({ headers, rows, renderRow }) => (
  <TableContainer
    component={Paper}
    variant="outlined"
    sx={{ borderRadius: "8px", borderColor: "#E2E8F0" }}
  >
    <Table size="small">
      <TableHead sx={{ bgcolor: "#F8FAFC" }}>
        <TableRow>
          {headers.map((h, i) => (
            <TableCell
              key={i}
              sx={{
                fontWeight: 600,
                color: "#475467",
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                py: 1,
                px: 1.5,
              }}
            >
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i} hover>
            {renderRow(row)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const WeeklySchedulerLogs = () => {
  const [page, setPage] = useState(1);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [expandedTables, setExpandedTables] = useState({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["weeklyScheduleLogs", page],
    queryFn: () => getWeeklyScheduleLogs(page),
    keepPreviousData: true,
    staleTime: 30000,
  });

  const handlePageChange = (event, value) => setPage(value);

  const toggleLogExpansion = (logId) =>
    setExpandedLogs((prev) => ({ ...prev, [logId]: !prev[logId] }));

  const toggleTableExpansion = (key) =>
    setExpandedTables((prev) => ({ ...prev, [key]: !prev[key] }));

  const formatDateTime = (dateTimeString) => {
    try {
      return format(parseISO(dateTimeString), "MMM d, yyyy h:mm a");
    } catch {
      return dateTimeString;
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch {
      return dateString;
    }
  };

  const SuccessfulAssignmentsTable = ({ assignments, logId }) => {
    const tableKey = `${logId}-success`;
    const isExpanded = expandedTables[tableKey];
    const displayed = isExpanded ? assignments : assignments.slice(0, 3);

    return (
      <Box sx={{ mb: 3 }}>
        <SectionHeader
          icon={CheckCircleIcon}
          label="Successful Assignments"
          count={assignments.length}
          color="#22C55E"
        />

        <SubTable
          headers={["Job ID", "Client Name", "Assignment ID", "Scheduled Date"]}
          rows={displayed}
          renderRow={(a) => (
            <>
              <TableCell sx={{ fontSize: "0.78rem", color: "#1A1A2E" }}>
                {a.job_id}
              </TableCell>
              <TableCell sx={{ fontSize: "0.78rem", color: "#1A1A2E" }}>
                {a.client_name}
              </TableCell>
              <TableCell sx={{ fontSize: "0.78rem", color: "#1A1A2E" }}>
                {a.assignment_id}
              </TableCell>
              <TableCell sx={{ fontSize: "0.78rem", color: "#475467" }}>
                {formatDate(a.scheduled_for)}
              </TableCell>
            </>
          )}
        />

        {assignments.length > 3 && (
          <Button
            size="small"
            onClick={() => toggleTableExpansion(tableKey)}
            sx={{
              mt: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              color: "#2469F0",
              "&:hover": { bgcolor: alpha("#2469F0", 0.04) },
            }}
          >
            {isExpanded ? "Show Less" : `Show All ${assignments.length}`}
          </Button>
        )}
      </Box>
    );
  };

  const SkippedAssignmentsTable = ({ assignments, logId }) => {
    const tableKey = `${logId}-skipped`;
    const isExpanded = expandedTables[tableKey];
    const displayed = isExpanded ? assignments : assignments.slice(0, 3);

    return (
      <Box sx={{ mb: 3 }}>
        <SectionHeader
          icon={CancelIcon}
          label="Skipped Assignments"
          count={assignments.length}
          color="#EF4444"
        />

        <SubTable
          headers={[
            "Assignment ID",
            "Client Name",
            "Reason",
            "Scheduled Start",
          ]}
          rows={displayed}
          renderRow={(a) => (
            <>
              <TableCell sx={{ fontSize: "0.78rem", color: "#1A1A2E" }}>
                {a.assignment_id}
              </TableCell>
              <TableCell sx={{ fontSize: "0.78rem", color: "#1A1A2E" }}>
                {a.client_name || "—"}
              </TableCell>
              <TableCell sx={{ fontSize: "0.78rem", color: "#475467" }}>
                {a.reason}
              </TableCell>
              <TableCell sx={{ fontSize: "0.78rem", color: "#475467" }}>
                {a.scheduled_start
                  ? formatDateTime(a.scheduled_start)
                  : "—"}
              </TableCell>
            </>
          )}
        />

        {assignments.length > 3 && (
          <Button
            size="small"
            onClick={() => toggleTableExpansion(tableKey)}
            sx={{
              mt: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              color: "#2469F0",
              "&:hover": { bgcolor: alpha("#2469F0", 0.04) },
            }}
          >
            {isExpanded ? "Show Less" : `Show All ${assignments.length}`}
          </Button>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
        Error loading scheduler logs: {error.message}
      </Alert>
    );
  }

  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / 10) || 1;

  return (
    <Box sx={{ width: "100%", px: 3, maxWidth: 1400, mx: "auto" }}>
      {/* HEADER */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              bgcolor: alpha("#2469F0", 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HistoryIcon sx={{ color: "#2469F0", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: "#0f172a" }}
            >
              Weekly Scheduler Logs
            </Typography>
            <Typography
              variant="caption"
              color="#64748b"
              sx={{ fontSize: "0.75rem" }}
            >
              View all scheduler run history and details
            </Typography>
          </Box>
        </Stack>
        <Chip
          label={`${totalCount} log${totalCount !== 1 ? "s" : ""}`}
          size="small"
          sx={{
            height: 22,
            fontSize: "0.65rem",
            fontWeight: 600,
            bgcolor: alpha("#2469F0", 0.08),
            color: "#2469F0",
          }}
        />
      </Box>

      {data?.results?.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            bgcolor: "#FFFFFF",
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: alpha("#94A3B8", 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
            }}
          >
            <HistoryIcon sx={{ fontSize: 24, color: "#94A3B8" }} />
          </Box>
          <Typography
            variant="body2"
            color="#475467"
            fontWeight={500}
            sx={{ fontSize: "0.85rem" }}
          >
            No scheduler logs found
          </Typography>
          <Typography
            variant="caption"
            color="#94A3B8"
            sx={{ fontSize: "0.7rem" }}
          >
            Logs will appear here once the scheduler runs
          </Typography>
        </Box>
      ) : (
        <>
          {data?.results?.map((log) => {
            const isCompleted = log.status === "Completed";
            return (
              <Card
                key={log.id}
                sx={{
                  mb: 2,
                  borderRadius: "12px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.02)",
                  overflow: "hidden",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: alpha("#2469F0", 0.4),
                    boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.08)",
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  {/* LOG HEADER */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ color: "#0F172A" }}
                      >
                        Log #{log.id}
                      </Typography>
                      <Chip
                        label={log.status}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          bgcolor: isCompleted
                            ? alpha("#22C55E", 0.1)
                            : alpha("#EF4444", 0.1),
                          color: isCompleted ? "#22C55E" : "#EF4444",
                        }}
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={2.5}
                      sx={{ ml: "auto", flexWrap: "wrap" }}
                    >
                      {[
                        { label: "Started", value: formatDateTime(log.start_time) },
                        { label: "Ended", value: formatDateTime(log.end_time) },
                        {
                          label: "Created",
                          value: log.jobs_created,
                          color: "#22C55E",
                        },
                        {
                          label: "Skipped",
                          value: log.assignments_skipped,
                          color: "#EF4444",
                        },
                      ].map((item) => (
                        <Box key={item.label}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.6rem",
                              textTransform: "uppercase",
                              fontWeight: 700,
                              color: "#94A3B8",
                              letterSpacing: "0.3px",
                              display: "block",
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              color: item.color || "#1A1A2E",
                            }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => toggleLogExpansion(log.id)}
                      endIcon={
                        expandedLogs[log.id] ? (
                          <ExpandLessIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 16 }} />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        borderRadius: "6px",
                        borderColor: "#D0D5DD",
                        color: "#344054",
                        px: 2,
                        py: 0.5,
                        "&:hover": {
                          borderColor: "#2469F0",
                          color: "#2469F0",
                          bgcolor: alpha("#2469F0", 0.04),
                        },
                      }}
                    >
                      {expandedLogs[log.id] ? "Hide Details" : "View Details"}
                    </Button>
                  </Box>

                  {/* EXPANDED CONTENT */}
                  {expandedLogs[log.id] && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2.5,
                        borderTop: "1px solid #E2E8F0",
                      }}
                    >
                      {log.successful_assignments?.length > 0 && (
                        <SuccessfulAssignmentsTable
                          assignments={log.successful_assignments}
                          logId={log.id}
                        />
                      )}

                      {log.skipped_assignments?.length > 0 && (
                        <SkippedAssignmentsTable
                          assignments={log.skipped_assignments}
                          logId={log.id}
                        />
                      )}

                      {log.errors && (
                        <Box sx={{ mb: 2 }}>
                          <SectionHeader
                            icon={ErrorIcon}
                            label="Errors"
                            color="#DC2626"
                          />
                          <Alert
                            severity="error"
                            sx={{ borderRadius: 2, fontSize: "0.8rem" }}
                          >
                            {log.errors}
                          </Alert>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontSize: "0.8rem",
                    fontWeight: 500,
                  },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default WeeklySchedulerLogs;